import React, { useEffect, useRef, useState } from 'react';

import { Button, Dropzone, Group, Image, Lightbox, LightboxTrigger, Stack, Text } from '@ekhodealer/ekho-common/rev';
import { Popup, PopupTrigger } from '@ekhodealer/ekho-common/rev';

import type { BlemishUploadTask } from '../../api/useBlemishImageUpload';

import { MediaGrid } from './MediaGrid';
import { UploadStatusPanel } from './UploadStatusPanel';

import './MediaModal.scss';

export interface MediaItem {
	/** Unique identifier for the media item */
	id: string;
	/** Public URL for the media resource */
	url: string;
	/** Whether this is an image or video */
	type: 'image' | 'video';
	/** e.g. "01:14" — only for video */
	durationLabel?: string;
}

export interface MediaModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Called when the modal requests to close */
	onClose: () => void;
	/** Called with final ordered items when user presses Done */
	onDone: (items: MediaItem[]) => void;
	/** New files dropped/selected via dropzone */
	onFilesAccepted?: (files: File[]) => void;
	/** Upload tasks — drives the upload status list and auto-appends successful items to grid */
	tasks?: BlemishUploadTask[];
	/** Initial media items to display */
	initialItems?: MediaItem[];
	/** Maximum number of items allowed; controls the Dropzone maxFiles limit */
	maxItems?: number;
}

export const MediaModal = ({
	isOpen,
	onClose,
	onDone,
	onFilesAccepted,
	tasks,
	initialItems = [],
	maxItems,
}: MediaModalProps): React.JSX.Element => {
	const [items, setItems] = useState<MediaItem[]>(initialItems);
	const [isSelectMode, setIsSelectMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState(new Set<string>());
	const [lightboxIndex, setLightboxIndex] = useState(-1);

	const prevIsOpenRef = useRef(false);
	const addedTaskIdsRef = useRef(new Set<string>());

	// Reset items and select state when the modal opens
	useEffect(() => {
		if (isOpen && !prevIsOpenRef.current) {
			setItems(initialItems);
			setIsSelectMode(false);
			setSelectedIds(new Set());
			addedTaskIdsRef.current = new Set();
		}
		prevIsOpenRef.current = isOpen;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on open transition, not on every initialItems change
	}, [isOpen]);

	// Append public URLs for newly completed uploads to the grid
	useEffect(() => {
		if (!tasks) return;
		const newItems: MediaItem[] = [];
		tasks.forEach((t) => {
			if (t.status === 'success' && t.publicUrl && !addedTaskIdsRef.current.has(t.id)) {
				addedTaskIdsRef.current.add(t.id);
				newItems.push({ id: t.id, url: t.publicUrl, type: 'image' });
			}
		});
		if (newItems.length > 0) {
			setItems((prev) => {
				const combined = [...prev, ...newItems];
				return maxItems !== undefined ? combined.slice(0, maxItems) : combined;
			});
		}
	}, [tasks, maxItems]);

	const handleItemClick = (index: number): void => {
		setLightboxIndex(index);
	};

	const handleOpenChange = (open: boolean): void => {
		if (!open) {
			onClose();
		}
	};

	const handleDone = (): void => {
		onDone(items);
		onClose();
	};

	const handleCancel = (): void => {
		setItems(initialItems);
		onClose();
	};

	const handleDeleteItem = (index: number): void => {
		setItems((prev) => prev.filter((_, i) => i !== index));
	};

	const handleToggleSelectMode = (): void => {
		setIsSelectMode((prev) => !prev);
		setSelectedIds(new Set());
	};

	const handleSelectionChange = (id: string): void => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const handleDeleteSelected = (): void => {
		setItems((prev) => prev.filter((item) => !selectedIds.has(item.id)));
		setSelectedIds(new Set());
		setIsSelectMode(false);
	};

	return (
		<>
			<PopupTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
				<Popup
					title='Manage the media'
					hasCloseButton
					isDismissable
					rounding='16'
					size='large'
					contentPadding={{ b: '0' }}>
					{() => (
						<div className='media-modal__content'>
							<div className='media-modal__body'>
								<Stack itemsSpacing='16'>
									<Stack itemsSpacing='8'>
										<Dropzone
											accepted={[
												{ type: 'image/jpeg' },
												{ type: 'image/png' },
											]}
											onFilesAccepted={onFilesAccepted}
											rounding='16'
											maxFiles={maxItems}
											title='Drag and drop images here'
											description='Best results: 10:7 (e.g., 1000×700, 1500×1050, 2000×1400). Min image size 1000×700. JPG/PNG.'
										/>
										{items.length > 0 && (
										<Group itemsAlignX='end'>
											<Group.Item>
												<Button
													variant='ghost'
													size='small'
													onPress={handleToggleSelectMode}>
													{isSelectMode ? 'Cancel' : 'Select'}
												</Button>
											</Group.Item>
										</Group>
									)}
									</Stack>

									{items.length > 0 && (
										<MediaGrid
											items={items}
											onItemsReordered={setItems}
											onItemClick={handleItemClick}
											onDeleteItem={handleDeleteItem}
											isSelectMode={isSelectMode}
											selectedIds={selectedIds}
											onSelectionChange={handleSelectionChange}
										/>
									)}

									{items.length === 0 && (
										<div className='media-modal__empty'>
											<Text size='bodySmall' color='secondary'>
												No media added yet
											</Text>
										</div>
									)}
								</Stack>
							</div>

							{tasks && tasks.length > 0 && (
								<UploadStatusPanel tasks={tasks} isOpen={isOpen} />
							)}

							<div className='media-modal__footer'>
								<Group itemsSpacing='8' itemsAlignX='end'>
									{isSelectMode && selectedIds.size > 0 && (
										<Group.Item>
											<Button
												variant='filled'
												size='medium'
												onPress={handleDeleteSelected}>
												{`Delete selected (${selectedIds.size})`}
											</Button>
										</Group.Item>
									)}
									<Group.Item>
										<Button variant='ghost' size='medium' onPress={handleCancel}>
											Cancel
										</Button>
									</Group.Item>
									<Group.Item>
										<Button variant='filled' size='medium' onPress={handleDone}>
											Done
										</Button>
									</Group.Item>
								</Group>
							</div>
						</div>
				)}
				</Popup>
			</PopupTrigger>
			<LightboxTrigger
				isOpen={lightboxIndex >= 0}
				onOpenChange={(open) => {
					if (!open) setLightboxIndex(-1);
				}}>
				<Lightbox
					items={items.map((item) => (
						<Image
							key={item.id}
							src={item.url}
							alt={item.type === 'video' ? 'Video thumbnail' : 'Full size image'}
							objectFit='contain'
							rounding='0'
						/>
					))}
					selectedIndex={lightboxIndex >= 0 ? lightboxIndex : 0}
					onSelectedIndexChange={setLightboxIndex}
					hasCloseButton
					hasItemStrip
				/>
			</LightboxTrigger>
		</>
	);
};
